import { Box, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { REFINANCE } from 'utils/constants';
import { usePolicyInfoContext } from '../contexts/policyInfoContext';

import {
  getAmount,
  LENDERS_ACTUAL,
  LENDERS_DISCLOSED,
  LENDERS_FEES,
  LENDERS_TOTAL,
  LendersPolicy,
  OWNERS_ACTUAL,
  OWNERS_DISCLOSED,
  OWNERS_FEES,
  policyData,
  policyDataRefinance,
  RefinanceCredits,
  SUM_LENDERS_ENDORSEMENTS,
  SUM_OWNERS_ENDORSEMENTS
} from '../services';
import { BalanceInfo } from './balance-info';

const useStyles = makeStyles((theme) => ({
  container: {
    width: "17rem",
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      display: 'flex',
      gap: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  buttonsContainer: { marginBottom: theme.spacing(4) },
  button: { textTransform: 'capitalize' },
  calculatorItem: {
    [theme.breakpoints.down('lg')]: {
      flex: 1,
      minWidth: 0,
    },
  },
  spacerContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('lg')]: {
      // horizontal gap comes from `container`'s flex `gap` in this row layout instead.
      marginTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      // container is a column again here, so restore the vertical gap between the two panels.
      marginTop: theme.spacing(2),
    },
  },
}));

type BalanceProps = {
  typeTransaction: string;
};

const Balance: FC<BalanceProps> = ({ typeTransaction }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const context = usePolicyInfoContext();
  const { accounting, selectedTab } = context || {};

  const policyBalance = useMemo(() => {
    const lendersActual = getAmount(
      accounting?.find((acc) => acc.code.includes(LENDERS_ACTUAL))
    );
    const lendersDisclosed = getAmount(
      accounting?.find((acc) => acc.code.includes(LENDERS_DISCLOSED))
    );
    const lendersFees = getAmount(
      accounting?.find((acc) => acc.code.includes(LENDERS_FEES))
    );
    const lendersEndorsements = getAmount(
      accounting?.find((acc) => acc.code.includes(SUM_LENDERS_ENDORSEMENTS))
    );

    const ownersDisclosed = getAmount(
      accounting?.find((acc) => acc.code.includes(OWNERS_DISCLOSED))
    );
    const ownersActual = getAmount(
      accounting?.find((acc) => acc.code.includes(OWNERS_ACTUAL))
    );
    const ownersFees = getAmount(
      accounting?.find((acc) => acc.code.includes(OWNERS_FEES))
    );
    const ownersEndorsements = getAmount(
      accounting?.find((acc) => acc.code.includes(SUM_OWNERS_ENDORSEMENTS))
    );

    const refinanceCredits = getAmount(
      accounting?.find((acc) => acc.code.includes(RefinanceCredits))
    );

    const lendersTotal = getAmount(
      accounting?.find((acc) => acc.code.includes(LENDERS_TOTAL))
    );

    const lendersPolicy = getAmount(
      accounting?.find((acc) => acc.code.includes(LendersPolicy))
    );

    const mapBalances = new Map();
    const balancesDisclosed = {
      owners_policy_tab: policyData(
        t,
        ownersDisclosed,
        ownersEndorsements,
        ownersFees
      ),
      lenders_policy_tab: policyData(
        t,
        lendersDisclosed,
        lendersEndorsements,
        lendersFees
      ),
    };

    const balancesActual = {
      owners_policy_tab: policyData(
        t,
        ownersActual,
        ownersEndorsements,
        ownersFees
      ),
      lenders_policy_tab: policyData(
        t,
        lendersActual,
        lendersEndorsements,
        lendersFees,
      ),
    };

    const balancesRefinance = {
      lenders_policy: policyDataRefinance({
        t,
        lenderEndorsements: lendersEndorsements,
        lenderFees: lendersFees,
        policy: lendersPolicy,
        credits: refinanceCredits,
        total: lendersTotal
      }),
    };

    mapBalances.set('disclosed', balancesDisclosed);
    mapBalances.set('actual', balancesActual);
    mapBalances.set('refinance', balancesRefinance);

    return mapBalances;
  }, [accounting, t]);

  return (
    <Box className={classes.container}>
      {selectedTab && (
        <>
          {typeTransaction !== REFINANCE && (
            <Box className={classes.calculatorItem}>
              <Paper>
                <BalanceInfo
                  title={t('orders:disclosed')}
                  data={policyBalance.get("disclosed")[selectedTab?.id!]}
                />
              </Paper>
            </Box>
          )}
          <Box className={`${classes.calculatorItem} ${classes.spacerContainer}`}>
            <Paper>
              <BalanceInfo
                title={t('orders:actual')}
                data={policyBalance.get("actual")[selectedTab?.id!]}
              />
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Balance;
